import { RestartAltRounded } from '@mui/icons-material';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useRandomPassword, useStateRef } from 'app/services';
import React, { forwardRef, useEffect, useImperativeHandle, useLayoutEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

const TokenForm = forwardRef((props, ref) => {
  const theme = useTheme();
  const [firstUpdate, setFirstUpdate, firstUpdateRef] = useStateRef(!!defaultValues);
  const { password, generateRandomPassword } = useRandomPassword();
  const { defaultValues, onSubmit } = props;
  const { control, reset, register, setValue, formState, handleSubmit } = useForm({
    defaultValues: {
      name: defaultValues ? defaultValues.name : '',
      login: defaultValues ? defaultValues.number : '',
      password: defaultValues ? '' : password,
      active: defaultValues ? defaultValues.active : 1,
      ip: defaultValues ? (defaultValues.ip ? defaultValues.ip : '') : '',
    },
  });

  const handleChangeIpInput = (value) => {
    switch (value[value.length - 1]) {
      case ' ':
      case ';':
        switch (value[value.length - 2]) {
          case undefined:
          case ';':
            setValue('ip', value.replace(/.$/, ''), {
              shouldDirty: true,
            });
            break;
          default:
            setValue('ip', value.replace(/.$/, ';'), {
              shouldDirty: true,
            });
        }

        break;
      case ',':
        setValue('ip', value.replace(/.$/, '.'), { shouldDirty: true });
        break;
    }
  };

  useImperativeHandle(ref, () => ({
    handleFormSubmit: () => {
      handleSubmit((data) =>
        onSubmit({
          name: data.name,
          login: data.login,
          password: data.password.length ? data.password : undefined,
          active: data.active,
          ip: data.ip ? data.ip : undefined,
        })
      )();
    },
  }));

  useLayoutEffect(() => {
    if (firstUpdateRef.current !== undefined) {
      setTimeout(() => setFirstUpdate(undefined), theme.transitions.duration.shorter);
    }
  }, [firstUpdateRef.current]);

  useEffect(() => {
    if (!defaultValues) {
      setValue('password', password, { shouldDirty: true });
    }
  }, [password]);

  useEffect(() => {
    if (defaultValues) {
      reset({
        name: defaultValues.name,
        login: defaultValues.login,
        password: defaultValues.password,
        active: defaultValues.active,
        ip: defaultValues.ip,
      });
    }
  }, [defaultValues]);

  return (
    <form>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <FormControl error={!!formState.errors.name} margin='normal' variant='outlined' fullWidth>
            <InputLabel disableAnimation={firstUpdate}>Название</InputLabel>
            <OutlinedInput
              label='Название'
              variant='outlined'
              notched={firstUpdate}
              fullWidth
              placeholder='Название токена'
              {...register('name', {
                pattern: /^([А-Яа-я0-9\s_-]{3,255})$/iu,
                required: true,
                minLength: 3,
              })}></OutlinedInput>
            <FormHelperText>Используйте в названии буквы кириллицы и цифры.</FormHelperText>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl error={!!formState.errors.login} margin='normal' variant='outlined' fullWidth>
            <InputLabel disableAnimation={firstUpdate}>Логин</InputLabel>
            <OutlinedInput
              label='Логин'
              variant='outlined'
              notched={firstUpdate}
              fullWidth
              placeholder='Логин'
              {...register('login', {
                pattern: /^([A-Za-z0-9\s]{3,255})$/iu,
                required: true,
                minLength: 3,
              })}></OutlinedInput>
            <FormHelperText>Используйте логин для определения токена в системе.</FormHelperText>
          </FormControl>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControl error={!!formState.errors.password} margin='normal' variant='outlined' fullWidth>
            <InputLabel disableAnimation={firstUpdate}>Пароль</InputLabel>
            <OutlinedInput
              label='Пароль'
              variant='outlined'
              notched={firstUpdate}
              fullWidth
              placeholder='Введите пароль'
              endAdornment={
                <InputAdornment position='end'>
                  <IconButton onClick={generateRandomPassword}>
                    <RestartAltRounded fontSize='small' />
                  </IconButton>
                </InputAdornment>
              }
              {...register('password', {
                required: false,
                pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9]).{6,255}$/,
              })}></OutlinedInput>
            {defaultValues ? (
              <FormHelperText>
                Если необходимо сменить пароль, то задайте новый в данном поле иначе оставьте его пустым. Пароль потребуется для создания
                заголовка Authorization при отправке запросов.
              </FormHelperText>
            ) : (
              <FormHelperText>
                Используйте сгенерированный пароль или задайте его самостоятельно. Пароль потребуется для создания заголовка Authorization
                при отправке запросов. Помните, что после создания пароль не будет отображаться в форме редактирования.
              </FormHelperText>
            )}
          </FormControl>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControl error={!!formState.errors.ip} margin='normal' variant='outlined' fullWidth>
            <InputLabel disableAnimation={firstUpdate}>Разрешенные IP адреса</InputLabel>
            <Controller
              name='ip'
              control={control}
              render={({ field }) => (
                <OutlinedInput
                  label='Разрешенные IP адреса'
                  variant='outlined'
                  notched={firstUpdate}
                  value={field.value}
                  fullWidth
                  onChange={field.onChange}
                  placeholder='192.168.0.1'
                  {...register('ip', {
                    pattern:
                      /^\*$|^(?:\d|1?\d\d|2[0-4]\d|25[0-5])(?:\.(?:\d|1?\d\d|2[0-4]\d|25[0-5])){3}(?:;(?:\d|1?\d\d|2[0-4]\d|25[0-5])(?:\.(?:\d|1?\d\d|2[0-4]\d|25[0-5])){3})*$/iu,
                    onChange: (e) => handleChangeIpInput(e.target.value),
                  })}></OutlinedInput>
              )}></Controller>

            <FormHelperText>
              Используйте IP в соответствие с форматом IPV4. Для указания нескольких IP адресов используйте формат
              123.124.125.126;123.124.125.127
            </FormHelperText>
          </FormControl>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControlLabel
            label='Активировать токен для отправки запросов'
            control={
              <Controller
                name='active'
                control={control}
                render={({ field }) => (
                  <Checkbox size='small' disableRipple checked={!!field.value} onChange={field.onChange} {...register(field.name)} />
                )}></Controller>
            }
          />
        </Grid>
      </Grid>
    </form>
  );
});

export default TokenForm;
